import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { OrdersService } from '../../orders.service';
import {
  Order,
  OrderItem,
  OrderItemStatus,
  OrderShipmentErrorResponse,
  OrderShipmentItemErrors,
  OrderShipmentReqBody,
  OrderStatus,
  OrderTypeTexts,
  Shipment,
  ShipmentStatus,
  Transporter,
} from '../../orders.models';
import { NO_ORDER_DETAILS_FOUND_ALERT_MSG } from '../../../common/helpers/constants';
import { SnackbarService } from '../../../services/snackbar.service';
import { SnackbarAlertStatus } from '../../../utils';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ProductionInventoryService } from '../../../production-inventory/services/production-inventory.service';
import { WarehouseBatchStock } from '../../../production-inventory/production-inventory.model';

interface ShipItemConfig {
  item: OrderItem;
  batchStocks: WarehouseBatchStock[];
  isLoadingBatches: boolean;
  selectedBatch: WarehouseBatchStock | null;
  box_quantity: number;
  error: string | null;
  isShipping: boolean;
}

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss'],
  standalone: false,
})
export class OrderDetailsComponent implements OnInit {
  @ViewChild('assignTransporterModal') assignTransporterModalRef!: TemplateRef<any>;
  @ViewChild('markDispatchedModal') markDispatchedModalRef!: TemplateRef<any>;
  @ViewChild('markDeliveredModal') markDeliveredModalRef!: TemplateRef<any>;
  @ViewChild('shipModal') shipModalRef!: TemplateRef<any>;

  order: Order | null = null;
  isLoading = false;
  cancellingItemId: string | null = null;

  // ── Ship modal state ─────────────────────────────────────────────────────
  protected shipItemConfigs: ShipItemConfig[] = [];
  protected shipTransporterId: string | null = null;
  protected shipDispatchDate: string = '';
  protected shipTrackingNumber: string = '';
  protected shipRemarks: string = '';
  protected shipExpectedDeliveryDate: string = '';
  protected showOptionalFields = false;
  protected isShipping = false;
  private shipModalInstance: NgbModalRef | null = null;

  // ── Shared shipment action state ─────────────────────────────────────────
  protected transporters: Transporter[] = [];
  protected isTransportersLoading = false;
  protected isActionLoading = false;
  protected activeShipment: Shipment | null = null;
  protected selectedTransporterId: string | null = null;
  protected expectedDeliveryDate: string = '';
  protected deliveryDate: string = '';
  protected readonly today: string = new Date().toISOString().split('T')[0];
  private modalRef: NgbModalRef | null = null;

  protected readonly ShipmentStatus = ShipmentStatus;
  protected readonly Math = Math;
  protected readonly OrderItemStatus = OrderItemStatus;
  protected readonly OrderStatus = OrderStatus;
  protected readonly OrderTypeTexts = OrderTypeTexts;
  protected readonly NO_ORDER_DETAILS_FOUND_ALERT_MSG = NO_ORDER_DETAILS_FOUND_ALERT_MSG;

  constructor(
    private route: ActivatedRoute,
    private ordersService: OrdersService,
    private productionInventoryService: ProductionInventoryService,
    private location: Location,
    private snackbarService: SnackbarService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadOrder(id);
  }

  loadOrder(id: string): void {
    this.isLoading = true;
    this.ordersService.get_order(id).subscribe({
      next: order => {
        this.order = order;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  goBack(): void {
    this.location.back();
  }

  downloadReceipt(): void {
    if (!this.order) return;
    this.ordersService.download_receipt(this.order.id).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt_${this.order?.order_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
    });
  }

  // ── Ship Modal ───────────────────────────────────────────────────────────

  protected openShipModal(): void {
    if (!this.order) return;

    const pendingItems = this.order.items.filter(i => i.pending_quantity > 0 && i.status !== OrderItemStatus.CANCELLED);
    if (pendingItems.length === 0) return;

    this.shipItemConfigs = pendingItems.map(item => ({
      item,
      batchStocks: [],
      isLoadingBatches: true,
      selectedBatch: null,
      box_quantity: item.pending_quantity,
      error: null,
      isShipping: false,
    }));

    this.shipTransporterId = null;
    this.shipDispatchDate = '';
    this.shipTrackingNumber = '';
    this.shipRemarks = '';
    this.shipExpectedDeliveryDate = '';
    this.showOptionalFields = false;
    this.isShipping = false;

    this.shipModalInstance = this.modalService.open(this.shipModalRef, {
      backdrop: 'static',
      size: 'xl',
      scrollable: true,
    });

    if (this.transporters.length === 0) {
      this.isTransportersLoading = true;
      this.ordersService
        .get()
        .pipe(finalize(() => (this.isTransportersLoading = false)))
        .subscribe({ next: data => (this.transporters = data.results) });
    }

    for (const config of this.shipItemConfigs) {
      const packagingId = config.item.product_packaging.id;
      this.productionInventoryService
        .getWarehouseBatchStocks({ product_packaging: packagingId, page_size: 100 })
        .pipe(finalize(() => (config.isLoadingBatches = false)))
        .subscribe({
          next: response => {
            config.batchStocks = response.results || [];
            if (config.batchStocks.length === 1) {
              this.selectBatch(config, config.batchStocks[0]);
            }
          },
        });
    }
  }

  protected selectBatch(config: ShipItemConfig, batch: WarehouseBatchStock): void {
    config.selectedBatch = batch;
    config.box_quantity = Math.min(config.item.pending_quantity, +batch.boxes_available);
    config.error = null;
  }

  protected get configuredItemsCount(): number {
    return this.shipItemConfigs.filter(c => c.selectedBatch !== null).length;
  }

  protected get uniqueBatchesCount(): number {
    return new Set(
      this.shipItemConfigs.filter(c => c.selectedBatch !== null).map(c => c.selectedBatch!.production_entry)
    ).size;
  }

  /**
   * Whether the shipment can be created and dispatched in one go. Same rule as
   * `canMarkDispatched` on an existing shipment: a transporter must be set, because the
   * backend refuses a dispatch without one. The expected delivery date matches what the
   * standalone Mark Dispatched modal already demands.
   */
  protected get canCreateAndDispatch(): boolean {
    return (
      this.configuredItemsCount > 0 && !!this.shipTransporterId && !!this.shipExpectedDeliveryDate && !this.isShipping
    );
  }

  /** Why Create & Dispatch is disabled, so the admin is not left guessing. */
  protected get dispatchBlockedReason(): string | null {
    if (this.configuredItemsCount === 0) return null;
    if (!this.shipTransporterId) return 'Pick a transporter to dispatch now.';
    if (!this.shipExpectedDeliveryDate) return 'Set an expected delivery date to dispatch now.';

    return null;
  }

  protected confirmShipment(dispatch: boolean = false): void {
    const toShip = this.shipItemConfigs.filter(c => c.selectedBatch !== null);
    if (toShip.length === 0 || this.isShipping) return;
    if (dispatch && !this.canCreateAndDispatch) return;
    if (!this._validateItems(toShip)) return;

    // One request, one shipment: every configured item travels as a line of `items`.
    const body: OrderShipmentReqBody = {
      items: toShip.map(config => ({
        order_item_id: config.item.id,
        warehouse_batch_stock_id: config.selectedBatch!.id,
        box_quantity: config.box_quantity,
      })),
      ...(this.shipTransporterId ? { transporter_id: this.shipTransporterId } : {}),
      ...(this.shipDispatchDate ? { dispatch_date: this.shipDispatchDate } : {}),
      ...(this.shipTrackingNumber ? { tracking_number: this.shipTrackingNumber } : {}),
      ...(this.shipRemarks ? { remarks: this.shipRemarks } : {}),
    };

    this.isShipping = true;
    for (const config of toShip) {
      config.isShipping = true;
      config.error = null;
    }

    this.ordersService.create_shipment(body).subscribe({
      next: shipment => {
        for (const config of toShip) config.isShipping = false;

        if (dispatch) {
          this._dispatchShipment(shipment);
          return;
        }

        this.isShipping = false;
        this.snackbarService.show(
          `Shipment ${shipment.shipment_number} created with ${toShip.length} item${toShip.length !== 1 ? 's' : ''}.`,
          SnackbarAlertStatus.SUCCESS
        );
        this.loadOrder(this.order!.id);
        this.shipModalInstance?.close();
      },
      error: err => {
        this.isShipping = false;
        for (const config of toShip) config.isShipping = false;
        this._applyShipmentErrors(err, toShip);
      },
    });
  }

  /**
   * Checks the totals the backend checks, so the admin is not round-tripping. Boxes are
   * summed per order item and per batch rather than read off a single item, because two
   * items can draw on the same batch and the backend rejects the whole shipment over it.
   */
  private _validateItems(toShip: ShipItemConfig[]): boolean {
    for (const config of toShip) config.error = null;

    let valid = true;
    const boxesPerItem = new Map<string, number>();
    const boxesPerBatch = new Map<string, number>();

    for (const config of toShip) {
      const quantity = Number(config.box_quantity);
      if (!Number.isInteger(quantity) || quantity < 1) {
        config.error = 'Boxes must be a whole number, 1 or more.';
        valid = false;
        continue;
      }

      boxesPerItem.set(config.item.id, (boxesPerItem.get(config.item.id) ?? 0) + quantity);
      const batchId = config.selectedBatch!.id;
      boxesPerBatch.set(batchId, (boxesPerBatch.get(batchId) ?? 0) + quantity);
    }

    if (!valid) return false;

    for (const [itemId, total] of boxesPerItem) {
      const config = toShip.find(c => c.item.id === itemId)!;
      if (total <= config.item.pending_quantity) continue;

      const message = `Cannot ship ${total} boxes. Only ${config.item.pending_quantity} boxes pending.`;
      for (const other of toShip.filter(c => c.item.id === itemId)) other.error = message;
      valid = false;
    }

    for (const [batchId, total] of boxesPerBatch) {
      const batch = toShip.find(c => c.selectedBatch!.id === batchId)!.selectedBatch!;
      const available = Math.floor(+batch.boxes_available);
      if (total <= available) continue;

      const message = `Insufficient stock for batch '${batch.batch_number}'. Available: ${available} boxes, requested: ${total} boxes.`;
      for (const other of toShip.filter(c => c.selectedBatch!.id === batchId)) other.error = message;
      valid = false;
    }

    return valid;
  }

  /**
   * Per-line errors come back positional, one entry per item sent, so they map straight
   * onto the configs in the order they were sent. A whole-request error is not positional
   * and belongs to no single item, so it goes to the snackbar. Nothing was created on a
   * 400 and no stock moved: the modal stays open for the admin to fix and resubmit.
   */
  private _applyShipmentErrors(err: any, toShip: ShipItemConfig[]): void {
    const body: OrderShipmentErrorResponse = err?.error ?? {};
    for (const config of toShip) config.error = null;

    const items = body.items;
    if (Array.isArray(items) && items.length > 0) {
      if (typeof items[0] === 'string') {
        this.snackbarService.show((items as string[]).join(' '), SnackbarAlertStatus.ERROR, 8000);
        return;
      }

      let mapped = false;
      (items as OrderShipmentItemErrors[]).forEach((entry, index) => {
        const messages = [
          ...(entry?.order_item_id ?? []),
          ...(entry?.warehouse_batch_stock_id ?? []),
          ...(entry?.box_quantity ?? []),
        ];
        if (messages.length === 0 || !toShip[index]) return;

        toShip[index].error = messages.join(' ');
        mapped = true;
      });

      if (mapped) {
        this.snackbarService.show(
          'Nothing was created. Fix the items flagged below and confirm again.',
          SnackbarAlertStatus.ERROR,
          8000
        );
        return;
      }
    }

    this.snackbarService.show(
      body.detail || body.non_field_errors?.[0] || 'Failed to create the shipment.',
      SnackbarAlertStatus.ERROR,
      8000
    );
  }

  /**
   * Dispatches the shipment that was just created. A failure here leaves a real shipment on
   * the order, so it is reported as a partial result rather than a plain failure: the work
   * is not lost, it is sitting as pending.
   */
  private _dispatchShipment(shipment: Shipment): void {
    this.ordersService
      .update_shipment_status(shipment.id, {
        status: ShipmentStatus.DISPATCHED,
        delivery_date: this.shipExpectedDeliveryDate,
      })
      .pipe(finalize(() => (this.isShipping = false)))
      .subscribe({
        next: () => {
          this.snackbarService.show(
            `Shipment ${shipment.shipment_number} created and dispatched.`,
            SnackbarAlertStatus.SUCCESS
          );
          this.loadOrder(this.order!.id);
          this.shipModalInstance?.close();
        },
        error: err => {
          const detail = err?.error?.detail || 'it could not be dispatched.';
          this.snackbarService.show(
            `Shipment ${shipment.shipment_number} was created but is still pending: ${detail} Dispatch it from the shipments below.`,
            SnackbarAlertStatus.ERROR,
            8000
          );
          this.loadOrder(this.order!.id);
          this.shipModalInstance?.close();
        },
      });
  }

  protected cancelItem(item: OrderItem): void {
    this.cancellingItemId = item.id;
    this.ordersService
      .cancel_item(item.id)
      .pipe(finalize(() => (this.cancellingItemId = null)))
      .subscribe({
        next: (orderItem: OrderItem) => {
          const idx = this.order?.items?.findIndex(i => i.id === orderItem.id);
          if (idx !== undefined && idx > -1) {
            this.order!.items[idx] = orderItem;
          }
        },
      });
  }

  // ── Shipment action helpers ───────────────────────────────────────────────

  protected canAssignTransporter(shipment: Shipment): boolean {
    return shipment.status === ShipmentStatus.PENDING || shipment.status === ShipmentStatus.PACKED;
  }

  protected canMarkDispatched(shipment: Shipment): boolean {
    return (
      (shipment.status === ShipmentStatus.PENDING || shipment.status === ShipmentStatus.PACKED) &&
      !!shipment.transporter
    );
  }

  protected canMarkDelivered(shipment: Shipment): boolean {
    return shipment.status === ShipmentStatus.DISPATCHED || shipment.status === ShipmentStatus.IN_TRANSIT;
  }

  // ── Assign Transporter ───────────────────────────────────────────────────

  protected openAssignTransporterModal(shipment: Shipment): void {
    this.activeShipment = shipment;
    this.selectedTransporterId = shipment.transporter?.id || null;

    if (this.transporters.length === 0) {
      this.isTransportersLoading = true;
      this.ordersService
        .get()
        .pipe(finalize(() => (this.isTransportersLoading = false)))
        .subscribe({ next: data => (this.transporters = data.results) });
    }

    this.modalRef = this.modalService.open(this.assignTransporterModalRef, { backdrop: 'static' });
  }

  protected confirmAssignTransporter(): void {
    if (!this.activeShipment || !this.selectedTransporterId) return;
    this.isActionLoading = true;
    this.ordersService
      .update_shipment(this.activeShipment.id, { transporter: this.selectedTransporterId })
      .pipe(finalize(() => (this.isActionLoading = false)))
      .subscribe({
        next: updated => {
          this._patchShipment(updated);
          this.modalRef?.close();
        },
      });
  }

  // ── Mark Dispatched ──────────────────────────────────────────────────────

  protected openMarkDispatchedModal(shipment: Shipment): void {
    this.activeShipment = shipment;
    this.expectedDeliveryDate = '';
    this.modalRef = this.modalService.open(this.markDispatchedModalRef, { backdrop: 'static' });
  }

  protected confirmMarkDispatched(): void {
    if (!this.activeShipment || !this.expectedDeliveryDate) return;
    this.isActionLoading = true;
    this.ordersService
      .update_shipment_status(this.activeShipment.id, {
        status: 'dispatched',
        delivery_date: this.expectedDeliveryDate,
      })
      .pipe(finalize(() => (this.isActionLoading = false)))
      .subscribe({
        next: updated => {
          this._patchShipment(updated);
          this.modalRef?.close();
        },
      });
  }

  // ── Mark Delivered ───────────────────────────────────────────────────────

  protected openMarkDeliveredModal(shipment: Shipment): void {
    this.activeShipment = shipment;
    this.deliveryDate = this.today;
    this.modalRef = this.modalService.open(this.markDeliveredModalRef, { backdrop: 'static' });
  }

  protected confirmMarkDelivered(): void {
    if (!this.activeShipment || !this.deliveryDate) return;
    this.isActionLoading = true;
    this.ordersService
      .update_shipment_status(this.activeShipment.id, {
        status: 'delivered',
        delivery_date: this.deliveryDate,
      })
      .pipe(finalize(() => (this.isActionLoading = false)))
      .subscribe({
        next: updated => {
          this._patchShipment(updated);
          this.loadOrder(this.order!.id);
          this.modalRef?.close();
        },
      });
  }

  private _patchShipment(updated: Shipment): void {
    if (!this.order) return;
    const idx = this.order.shipments.findIndex(s => s.id === updated.id);
    if (idx !== -1) this.order.shipments[idx] = updated;
  }

  /** "5 Kg × 4 · Carton": the packaging line shown under every product name. */
  getPackagingLabel(item: OrderItem): string {
    const packaging = item.product_packaging;
    const parts = [`${packaging.packing_size} ${packaging.unit?.short_name ?? ''}`.trim()];

    if (packaging.nos) {
      parts.push(`× ${packaging.nos}`);
    }
    if (packaging.packaging_type) {
      parts.push(`· ${packaging.packaging_type.name}`);
    }

    return parts.join(' ');
  }

  getProductTitle(item: OrderItem): string {
    const product = item.product_packaging.product;
    if (typeof product === 'string') return product;
    // @ts-ignore
    return product.name;
  }

  getInvoiceUrl(): string | null {
    if (!this.order || !this.order.documents) return null;
    const invoiceDoc = this.order.documents.find(doc => doc.document_type === 'invoice');
    return invoiceDoc ? invoiceDoc.file : null;
  }

  downloadInvoice(): void {
    const url = this.getInvoiceUrl();
    if (url) window.open(url, '_blank');
  }
}
